import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Pluralize,
  Text,
  SwitchRoot,
  SwitchThumb,
  toast,
} from 'ui'
import { useShiftDetail } from '../useShiftDetail'
import { ShiftDetail } from '../ShiftDetailProvider'
import {
  ROSTER_USER_STATUS,
  determineIfShiftRestrictedToRosters,
  getNumRosterWorkersAssignedToShift,
  getRosterWorkersOnShift,
} from 'lib/roster'
import { LucideInfo } from 'lucide-react'
import { AuthGuard } from 'components/auth'
import { PermissionEnum } from 'api/userPermissions'
import { RemoveRestrictionModal } from 'pages/HomePage/ShiftCard/RemoveRestrictionModal'
import { WorkersSelector } from 'components/WorkersSelector'
import { CompanyWorker, HypotheticalShift } from 'api/worker'
import { RosterSelector } from 'components/RosterSelector'
import { useUpdateShiftRosterMutation } from 'queries/shift/useUpdateShiftRosterMutation'
import { useUpdateRosterWorkersMutation } from 'queries/worker/useUpdateRosterWorkersMutation'
import { track } from 'lib/amplitude'
import useShiftTimeline from 'hooks/useShiftTimeline'
import { AvatarGroup, Tooltip, useAppTheme } from '@workwhile/ui'
import { getHypotheticalShiftsFromExistingShift } from 'lib/shift'
import { useWorkerStats } from 'pages/HomePage/ShiftCard/useWorkerStats'
import { TimelineShift } from 'api/shift'
import { SelectedWorkerStatus } from 'components/WorkersSelector/SelectedWorkerStatus'
import { useWorkerAvailability } from 'queries/worker'
import { UnavailableWorkersTooltip } from './UnavailableWorkersTooltip'
import { BundledInviteNote } from 'components/BundledShift/BundledInviteNote'
import { WILL_OR_HAS_WORKED_STATUSES } from 'lib/work'

type ShiftWithRosters = ShiftDetail & {
  rosters: NonNullable<ShiftDetail['rosters']>
}

const hasRosters = (shift: ShiftDetail | null): shift is ShiftWithRosters =>
  !!shift?.rosters?.length

export const ShiftDetailAudience = () => {
  const { shift } = useShiftDetail()

  if (!hasRosters(shift)) {
    return null
  }

  return <ShiftDetailAudienceContent shift={shift} />
}

type ShiftDetailAudienceContentProps = {
  shift: ShiftWithRosters
}

const ShiftDetailAudienceContent = ({
  shift,
}: ShiftDetailAudienceContentProps) => {
  const [showEditSelectionModal, setShowEditSelectionModal] = useState(false)
  const { declined } = useWorkerStats(shift as TimelineShift)
  const { colors } = useAppTheme()

  const { mutate: updateShiftRoster, isPending: isUpdatingShiftRosters } =
    useUpdateShiftRosterMutation({
      shiftId: shift.id,
      onSuccess() {
        setShowEditSelectionModal(false)
        toast.success('List updated successfully')
      },
      onError(err) {
        toast.error(err.message)
      },
    })

  const { mutate: updateRosterWorkers, isPending: isUpdatingRosterWorkers } =
    useUpdateRosterWorkersMutation({
      shiftId: shift.id,
      rosterId: shift.rosters[0].id,
      onSuccess() {
        setShowEditSelectionModal(false)
        toast.success('Workers updated successfully')
      },
      onError(err) {
        toast.error(err.message)
      },
    })

  const { isInProgress, isInPast } = useShiftTimeline(shift)

  const rosterWorkers = getRosterWorkersOnShift(shift)
  const numRosterWorkers = rosterWorkers.length
  const numWorkersAccepted = getNumRosterWorkersAssignedToShift(shift)
  const numDeclinedText = declined > 0 ? `, ${declined} declined` : ''

  const isRosterRestrictionActive = determineIfShiftRestrictedToRosters(shift)
  const isPermHold =
    isRosterRestrictionActive && shift.rostersHoldExpiresAt === null

  const isSpecificWorkerRequest = shift.rosters.some(
    (r) => r.isSpecificWorkerRequest
  )

  const { data: workerAvailabilities, isLoading: isAvailabilityLoading } =
    useWorkerAvailability({
      workerIds: rosterWorkers.map((w) => w.id),
      shiftIds: [shift.id],
    })

  const { unavailableWorkers, numUnavailableWorkers } = useMemo(() => {
    if (!workerAvailabilities) {
      return { unavailableWorkers: [], numUnavailableWorkers: 0 }
    }

    const unavailableWorkerIds = workerAvailabilities
      ?.filter((availability) => {
        // Skip if worker is unavailable
        if (!availability || !availability.available) {
          // Also check if worker is already scheduled for this shift
          const isScheduled = shift.work?.some(
            (work) =>
              Number(work.worker?.id) === Number(availability.workerId) &&
              WILL_OR_HAS_WORKED_STATUSES.includes(work.status || '')
          )
          // Only include in unavailable list if they're not already scheduled
          return !isScheduled
        }
        return false
      })
      .map((availability) => Number(availability.workerId))

    return {
      unavailableWorkers: rosterWorkers.filter((worker) =>
        unavailableWorkerIds.includes(Number(worker.id))
      ),
      numUnavailableWorkers: unavailableWorkerIds.length,
    }
  }, [rosterWorkers, workerAvailabilities, shift])

  /**
   * Given the existing selected workers, and the selected workers chosen by the user
   * Generate the new roster_users array to be sent to the backend
   * see data the PUT endpoint expects: https://github.com/workwhile/backend/blob/2f858909c738448ed9ee0a93e329bce65a01aca0/api/v1/company_roster.py/#L99-L100
   */
  const generateRosterUsersToUpdate = (selectedWorkers: CompanyWorker[]) => {
    const workersToBeActiveOnRoster = selectedWorkers.map((w) => ({
      worker_id: w.id,
      status: ROSTER_USER_STATUS.active,
    }))
    const workersToBeRemovedFromRoster = rosterWorkers
      .filter((w) => !selectedWorkers.some((sw) => sw.id === w.id))
      .map((w) => ({
        worker_id: w.id,
        status: ROSTER_USER_STATUS.removed,
      }))
    return [...workersToBeActiveOnRoster, ...workersToBeRemovedFromRoster]
  }

  const submitSelectedSpecificWorkers = (selectedWorkers: CompanyWorker[]) => {
    track('button_click, shift_detail_rosters_update_specific_workers')
    if (isInProgress || isInPast) {
      toast.error(
        'This shift has already started. You cannot update the audience for this shift. If you require assistance, please contact WorkWhile Support.'
      )
      return
    }
    updateRosterWorkers(generateRosterUsersToUpdate(selectedWorkers))
  }

  const hypotheticalShifts: Array<HypotheticalShift | null> = useMemo(
    () => getHypotheticalShiftsFromExistingShift(shift),
    [shift.location?.id, shift.position?.id, shift.startsAt, shift.endsAt]
  )

  return (
    <Box mb={4}>
      <Flex
        flexDirection={['column', 'column', 'row']}
        alignItems={['flex-start', 'flex-start', 'center']}
        justifyContent="space-between"
        mb={[3, 3, 0]}
      >
        <Box>
          <Heading level={2} fontWeight={2}>
            Special invites sent to{' '}
            <Pluralize
              count={numRosterWorkers}
              oneText="worker"
              manyText="workers"
            />
          </Heading>
          <BundledInviteNote shift={shift} />
        </Box>
        <AuthGuard.Permission value={PermissionEnum.ManageShifts}>
          <Button
            variant="secondary"
            kind="medium"
            onClick={() => setShowEditSelectionModal(true)}
          >
            {isInProgress || isInPast ? 'View' : 'View / Edit'}
          </Button>
          {isSpecificWorkerRequest ? (
            <WorkersSelector
              open={showEditSelectionModal}
              title="Edit Requested Workers"
              selected={rosterWorkers}
              onClose={() => setShowEditSelectionModal(false)}
              onChange={submitSelectedSpecificWorkers}
              loading={isUpdatingRosterWorkers}
              selectedWorkerStatusComponent={(worker) => (
                <SelectedWorkerStatus
                  worker={worker}
                  hypotheticalShifts={hypotheticalShifts}
                  existingShift={shift}
                />
              )}
            />
          ) : (
            <RosterSelector
              open={showEditSelectionModal}
              onClose={() => setShowEditSelectionModal(false)}
              selected={shift.rosters.map((r) => Number(r.id))}
              onChange={updateShiftRoster}
              loading={isUpdatingShiftRosters}
            />
          )}
        </AuthGuard.Permission>
      </Flex>
      <Flex flexDirection="row" alignItems="center" mb={3}>
        <Box mr={3}>
          <AvatarGroup
            max={5}
            users={rosterWorkers.map((worker) => ({
              userId: worker.id.toString(),
              photoUrl: worker?.profilePicUrl,
              name: worker.name,
            }))}
          />
        </Box>
        <Text>
          {numWorkersAccepted} accepted
          {numDeclinedText}
          {numUnavailableWorkers > 0 &&
            !isAvailabilityLoading &&
            !isInProgress &&
            !isInPast && (
              <Tooltip
                content={
                  <UnavailableWorkersTooltip
                    unavailableWorkers={unavailableWorkers}
                    workerAvailabilities={workerAvailabilities || []}
                  />
                }
                variant="neutral"
              >
                <Flex flexDirection="row" alignItems="center">
                  <Text
                    color="error"
                    mr={1}
                    style={{ textDecoration: 'underline' }}
                  >
                    {numUnavailableWorkers} worker
                    {numUnavailableWorkers !== 1 ? 's' : ''} unavailable
                  </Text>
                  <LucideInfo size={16} color={colors.error} />
                </Flex>
              </Tooltip>
            )}
        </Text>
      </Flex>
      {isPermHold ? (
        <Flex
          flexDirection={['column', 'column', 'row']}
          alignItems={['flex-start', 'flex-start', 'center']}
          mb={3}
          justifyContent="space-between"
          bg="neutrals.100"
          borderRadius={2}
          p={2}
          border="1px solid"
          borderColor="neutrals.200"
        >
          <Flex alignItems="center" mb={[3, 3, 0]}>
            <LucideInfo size={16} />
            <Text ml={3}>
              If requested workers are unable to attend, let other qualified
              workers accept.
            </Text>
          </Flex>
          <RemoveRestrictionModal
            shift={shift}
            trigger={
              <SwitchRoot checked={!isPermHold}>
                <SwitchThumb>{isPermHold ? 'OFF' : 'ON'}</SwitchThumb>
              </SwitchRoot>
            }
          ></RemoveRestrictionModal>
        </Flex>
      ) : null}
    </Box>
  )
}
